import { Step } from "react-joyride";

/**
 * Guided tour steps for the Initial Evaluation step.
 *
 * Each step targets a `data-testid` attribute rendered in the step.
 *
 * NOTE: the previous comment here claimed "if a target isn't present, Joyride will
 * auto-skip it". That is NOT true — Joyride warns about the unmounted target and the
 * tour stalls with nothing on screen, which is exactly how "Show Me" came to do
 * nothing for an RFx Lead who is not scoring. Every step below must target something
 * that is actually MOUNTED AND VISIBLE in the state it is built for; hidden tab panels
 * do not count. Use `buildInitialEvaluationSteps` rather than adding conditional
 * targets to a single static list.
 */
const scoringSteps: Step[] = [
	{
		target: '[data-testid="supplier-response-table"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Supplier Submissions</h3>
				<p>
					This table lists all suppliers who have submitted responses. Click
					on a supplier row to open the scoring modal and begin evaluating.
				</p>
				<p className="mt-2 text-gray-500">
					The status column shows your scoring progress for each supplier.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="tab-my-clarifications"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Clarifications</h3>
				<p>
					Switch to this tab to create clarification questions for suppliers.
					Use this to request more information or ask suppliers to clarify
					certain aspects of their submission - they are not sent at this stage.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="supplier-response-actions"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">View Supplier Response</h3>
				<p>
					You can view the suppliers RFx response by opening it in a new browser tab, or using the PDF button to download a copy of their submission. Use this to review the suppliers response in full during scoring.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];

/**
 * Steps for an RFx Lead in panel mode who has NOT opted in to scoring.
 *
 * `LeadScoringSection` is not mounted in that state, so `supplier-response-table` and
 * `supplier-response-actions` do not exist — and the panel-members content sits in a
 * CSS-hidden tab panel. Only the toggle and the tab buttons are on screen, so those are
 * the only things worth pointing at.
 */
const nonScoringLeadSteps: Step[] = [
	{
		target: '[data-testid="lead-scoring-toggle"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Are you scoring?</h3>
				<p>
					You are not currently scoring this evaluation. Turn this on if you
					want to score the suppliers yourself alongside your panel members.
				</p>
				<p className="mt-2 text-gray-500">
					Leaving it off is fine — you can still run the panel and review
					everyone else&apos;s scores.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="tab-panel-members"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Panel Members</h3>
				<p>
					Track each panel member&apos;s scoring progress here, and chase anyone
					with outstanding scores. Member Scores and the averages appear on this
					tab once scores come in.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="tab-my-clarifications"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Clarifications</h3>
				<p>
					Raise clarification questions for suppliers from this tab. They are
					not sent to suppliers at this stage.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];

export interface InitialEvaluationGuideOptions {
	/**
	 * PRO-664: whether the RFx Lead has opted in to scoring. When false the scoring UI
	 * is not mounted at all, so the scoring steps would stall the tour.
	 */
	isLeadScoring: boolean;
}

export const buildInitialEvaluationSteps = ({
	isLeadScoring,
}: InitialEvaluationGuideOptions): Step[] =>
	isLeadScoring ? scoringSteps : nonScoringLeadSteps;

/** Default export for solo mode, where the lead always scores. */
export const initialEvaluationSteps: Step[] = scoringSteps;

/**
 * Guided tour steps for the Initial Evaluation scoring modal.
 */
export const scoringModalSteps: Step[] = [
	{
		target: '[data-testid="scoring-modal-header"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Supplier Details</h3>
				<p>
					View the supplier&apos;s submission details, download their response
					as a PDF, or view their full submission. The progress bar shows how
					many criteria you&apos;ve scored.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="scoring-grid"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Scoring Grid</h3>
				<p>
					Score each criterion by entering a value in the score field. Add
					comments to explain your reasoning — these are visible to the RFx Lead.
				</p>
				<p className="mt-2 text-gray-500">
					Scores save automatically as you type.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="toggle-instructions-btn"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Scoring Guide</h3>
				<p>
					Toggle this to open the scoring instructions panel. It shows the
					rubric for the currently focused score, helping you assign consistent
					and fair scores.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="supplier-select"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Switch Supplier</h3>
				<p>
					Use this dropdown to switch between suppliers without closing the
					modal. Your scores for the current supplier are saved automatically.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];
